const MILLISECONDS_PER_DAY = 86_400_000;

const relativeFormatter = new Intl.RelativeTimeFormat('en-GB', { numeric: 'always' });

const formatRelativeValue = (value: number, unit: Intl.RelativeTimeFormatUnit): string =>
    relativeFormatter.format(value, unit);

const compactUnits: Record<Intl.RelativeTimeFormatUnit, string> = {
    second: 'sec',
    seconds: 'sec',
    minute: 'min',
    minutes: 'min',
    hour: 'hr',
    hours: 'hr',
    day: 'd',
    days: 'd',
    week: 'wk',
    weeks: 'wk',
    month: 'mo',
    months: 'mo',
    quarter: 'qtr',
    quarters: 'qtr',
    year: 'yr',
    years: 'yr',
};

const formatCompactValue = (value: number, unit: Intl.RelativeTimeFormatUnit): string =>
    `${Math.abs(value)} ${compactUnits[unit]}`;

const relativeUnit = (differenceInDays: number): { value: number; unit: Intl.RelativeTimeFormatUnit } => {
    const absoluteDays = Math.abs(differenceInDays);

    if (absoluteDays < 14) return { value: Math.round(differenceInDays), unit: 'day' };
    if (absoluteDays < 60) return { value: Math.round(differenceInDays / 7), unit: 'week' };
    if (absoluteDays < 730) return { value: Math.round(differenceInDays / 30.4375), unit: 'month' };
    return { value: Math.round(differenceInDays / 365.25), unit: 'year' };
};

const parseCalendarDate = (value: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const [, year, month, day] = match;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return parsed.getFullYear() === Number(year)
        && parsed.getMonth() === Number(month) - 1
        && parsed.getDate() === Number(day)
        ? parsed
        : null;
};

const calendarDayDifference = (date: Date, comparisonDate: Date): number => {
    const dateUtc = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const comparisonUtc = Date.UTC(
        comparisonDate.getFullYear(),
        comparisonDate.getMonth(),
        comparisonDate.getDate()
    );
    return Math.round((dateUtc - comparisonUtc) / MILLISECONDS_PER_DAY);
};

const toCalendarDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export interface LastCheckDisplay {
    primary: string;
    secondary: string;
    copyValue?: string;
    accessibleLabel?: string;
    dateTime?: string;
}

export const getLastCheckDisplay = (value?: string, now: Date = new Date()): LastCheckDisplay => {
    if (!value) return { primary: 'Never checked', secondary: 'No result available' };

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return { primary: 'Unknown', secondary: 'Invalid date' };

    const differenceInSeconds = (date.getTime() - now.getTime()) / 1000;
    const absoluteSeconds = Math.abs(differenceInSeconds);
    let relativeLabel: string;
    let accessibleLabel: string;

    if (absoluteSeconds < 60) {
        relativeLabel = 'just now';
        accessibleLabel = 'Last checked just now';
    } else if (absoluteSeconds < 3600) {
        const value = Math.round(differenceInSeconds / 60);
        relativeLabel = value > 0
            ? `in ${formatCompactValue(value, 'minute')}`
            : `${formatCompactValue(value, 'minute')} ago`;
        accessibleLabel = `Last checked ${formatRelativeValue(value, 'minute')}`;
    } else if (absoluteSeconds < 86_400) {
        const value = Math.round(differenceInSeconds / 3600);
        relativeLabel = value > 0
            ? `in ${formatCompactValue(value, 'hour')}`
            : `${formatCompactValue(value, 'hour')} ago`;
        accessibleLabel = `Last checked ${formatRelativeValue(value, 'hour')}`;
    } else {
        const differenceInDays = differenceInSeconds / 86_400;
        const relative = relativeUnit(differenceInDays);
        relativeLabel = relative.value > 0
            ? `in ${formatCompactValue(relative.value, relative.unit)}`
            : `${formatCompactValue(relative.value, relative.unit)} ago`;
        accessibleLabel = `Last checked ${formatRelativeValue(relative.value, relative.unit)}`;
    }

    const exactDate = date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    const exactTime = date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
        });

    return {
        primary: exactDate,
        secondary: `${exactTime} · ${relativeLabel}`,
        copyValue: `${exactDate}, ${exactTime}`,
        accessibleLabel,
        dateTime: date.toISOString(),
    };
};

export type ExpiryStatus = 'missing' | 'current' | 'due-soon' | 'expired';

export interface ComplianceExpiryDisplay {
    date: string;
    relative: string;
    status: ExpiryStatus;
    accessibleLabel?: string;
    dateTime?: string;
}

export const getComplianceExpiryDisplay = (
    value?: string | null,
    now: Date = new Date()
): ComplianceExpiryDisplay => {
    if (!value) return { date: 'Not recorded', relative: 'No date recorded', status: 'missing' };

    const expiry = parseCalendarDate(value);
    if (!expiry) return { date: 'Unknown', relative: 'Invalid date', status: 'missing' };

    const differenceInDays = calendarDayDifference(expiry, now);
    const relative = relativeUnit(differenceInDays);
    const relativeText = formatRelativeValue(relative.value, relative.unit);
    const compactRelativeText = formatCompactValue(relative.value, relative.unit);
    const status: ExpiryStatus = differenceInDays < 0
        ? 'expired'
        : differenceInDays <= 30
            ? 'due-soon'
            : 'current';

    let relativeLabel: string;
    let accessibleLabel: string;
    if (differenceInDays < 0) {
        relativeLabel = `${compactRelativeText} overdue`;
        accessibleLabel = `Expired ${formatRelativeValue(-Math.abs(relative.value), relative.unit)}`;
    } else if (differenceInDays === 0) {
        relativeLabel = 'Due today';
        accessibleLabel = 'Expires today';
    } else if (differenceInDays === 1) {
        relativeLabel = 'Due tomorrow';
        accessibleLabel = 'Expires tomorrow';
    } else if (differenceInDays <= 30) {
        relativeLabel = `${compactRelativeText} remaining`;
        accessibleLabel = `Due ${relativeText}`;
    } else {
        relativeLabel = '';
        accessibleLabel = `Expires ${relativeText}`;
    }

    return {
        date: expiry.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        relative: relativeLabel,
        status,
        accessibleLabel,
        dateTime: toCalendarDateString(expiry),
    };
};
